// CookieYes consent gate for analytics.
//
// CookieYes (loaded via GTM) owns the banner UI and cookie; this only reads
// its decision, so nothing analytics-related runs until the visitor has
// allowed the `analytics` category.
//
// https://www.cookieyes.com/documentation/retrieving-consent-data-using-api-getckyconsent/

// Always the default English key, even when the category is renamed in the
// CookieYes dashboard.
const ANALYTICS_CATEGORY = 'analytics'
const BANNER_LOAD_EVENT = 'cookieyes_banner_load'
const CONSENT_UPDATE_EVENT = 'cookieyes_consent_update'

const subscribers = new Set()

// Denied until CookieYes says otherwise. If the banner is blocked or never
// loads, analytics stay off rather than racing an answer that never arrives.
let analyticsAllowed = false

export const isAnalyticsAllowed = () => analyticsAllowed

// `getCkyConsent` exists only once the banner has loaded. Reading `categories`
// rather than `isUserActionCompleted` keeps the answer correct under both
// consent laws: CookieYes already resolves opt-in (GDPR) vs opt-out (CCPA).
function readConsent(categories) {
  const source = categories ?? window.getCkyConsent?.()?.categories
  return !!source?.[ANALYTICS_CATEGORY]
}

function update(allowed) {
  if (allowed === analyticsAllowed) return

  analyticsAllowed = allowed
  subscribers.forEach((subscriber) => subscriber(allowed))
}

// Calls `subscriber` on every change, and immediately if consent is already in
// — the banner can resolve before this module is evaluated. Returns an
// unsubscribe, for subscribers tied to a component mount.
export function onAnalyticsConsentChange(subscriber) {
  subscribers.add(subscriber)
  if (analyticsAllowed) subscriber(true)

  return () => subscribers.delete(subscriber)
}

function initialize() {
  update(readConsent())

  // Carries the same payload as `getCkyConsent`; fall back to the API if not.
  document.addEventListener(BANNER_LOAD_EVENT, (event) =>
    update(readConsent(event?.detail?.categories)),
  )
  document.addEventListener(CONSENT_UPDATE_EVENT, (event) => {
    const { accepted } = event?.detail ?? {}
    // Fall back to the API when the payload isn't the documented shape, so a
    // banner change can never leave analytics on by omission.
    update(
      Array.isArray(accepted)
        ? accepted.includes(ANALYTICS_CATEGORY)
        : readConsent(),
    )
  })
}

initialize()
